import { useId } from 'react';
import { BRAND_BLUE, lockup, mark } from '@/lib/logo';
import { cn } from '@/lib/cn';

/**
 * The logo, drawn inline.
 *
 * Inline rather than an <img> for two reasons. The wordmark takes its colours from the theme
 * tokens, so the same component is correct on the light theme, on the dark one, and on any
 * accent-coloured surface, instead of the app having to pick between two files and get it wrong
 * once. And an inline mark can be animated with the rest of the header, which an image cannot.
 *
 * The shapes come from `@/lib/logo`, which is generated from the same geometry as the standalone
 * files in `public/brand` — so the logo in the header and the logo somebody downloads for a press
 * page are guaranteed to be the same drawing.
 */

/**
 * The badge's gradients.
 *
 * Declared per instance with a `useId` prefix: two logos on one page — the header and the footer
 * both have one — would otherwise share an id, and the first one mounted would quietly define the
 * fill for both.
 */
function Enamel({ id }: { id: string }) {
  return (
    <defs>
      <linearGradient
        id={`${id}-enamel`}
        gradientUnits="userSpaceOnUse"
        x1={0}
        y1={0}
        x2={mark.size * 0.7}
        y2={mark.size}
      >
        <stop offset="0%" stopColor={BRAND_BLUE.light} />
        <stop offset="48%" stopColor={BRAND_BLUE.mid} />
        <stop offset="100%" stopColor={BRAND_BLUE.deep} />
      </linearGradient>

      {/* The sheen across the upper left. Without it the badge is a blue square. */}
      <linearGradient
        id={`${id}-gloss`}
        gradientUnits="userSpaceOnUse"
        x1={0}
        y1={0}
        x2={mark.size * 0.4}
        y2={mark.size}
      >
        <stop offset="0%" stopColor="#ffffff" stopOpacity={BRAND_BLUE.gloss} />
        <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

/** The badge itself, always drawn in the mark's own 512-unit square and positioned by a transform. */
function Badge({ id }: { id: string }) {
  return (
    <>
      <rect width={mark.size} height={mark.size} rx={mark.radius} fill={`url(#${id}-enamel)`} />
      <rect width={mark.size} height={mark.size} rx={mark.radius} fill={`url(#${id}-gloss)`} />
      <path d={mark.glyph} fill="#ffffff" />
      {/* The centre line, painted back in the badge's colour so it follows the same gradient. */}
      <path d={mark.dashes} fill={`url(#${id}-enamel)`} />
    </>
  );
}

interface LogoProps {
  className?: string;
  /** Leave unset where a visible "KojaKola" sits beside it, which is most places. */
  title?: string;
  /**
   * Tilts the badge when an ancestor marked `group` is hovered. On for the header and footer
   * links, off everywhere the logo is not something you can click.
   */
  interactive?: boolean;
}

/** Badge and wordmark together. Sized by height — pass `h-8` and let the width follow. */
export function Logo({ className, title, interactive = false }: LogoProps) {
  const id = useId();
  const scale = lockup.badge.size / mark.size;

  return (
    <svg
      viewBox={`0 0 ${lockup.width} ${lockup.height}`}
      className={cn('block w-auto text-body', className)}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {title && <title>{title}</title>}
      <Enamel id={id} />

      <g transform={`translate(${lockup.badge.x} ${lockup.badge.y}) scale(${scale})`}>
        {/*
          The hover transform lives on an inner group. A CSS transform replaces the `transform`
          attribute rather than composing with it, so animating the positioned group would fling
          the badge out of the lockup on first hover.
        */}
        <g
          className={cn(
            interactive &&
              'origin-center transition duration-500 ease-out-expo [transform-box:fill-box] group-hover:-rotate-6 group-hover:scale-105',
          )}
        >
          <Badge id={id} />
        </g>
      </g>

      <path d={lockup.koja} fill="currentColor" />
      <path d={lockup.kola} fill="var(--accent)" />
      <path d={lockup.suffix} fill="var(--text-faint)" />
    </svg>
  );
}
